/**
 * Simple logger interface for package-level logging
 * Provides structured logging without external dependencies
 */

export interface Logger {
  trace(message: string, context?: Record<string, unknown>): void;
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
  fatal(message: string, context?: Record<string, unknown>): void;
}

/**
 * Console-based logger implementation
 * Used by packages that need basic logging without server dependencies
 */
class ConsoleLogger implements Logger {
  private formatMessage(
    level: string,
    message: string,
    context?: Record<string, unknown>,
  ): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : "";
    return `[${timestamp}] ${level}: ${message}${contextStr}`;
  }

  trace(message: string, context?: Record<string, unknown>): void {
    console.log(this.formatMessage("TRACE", message, context));
  }

  debug(message: string, context?: Record<string, unknown>): void {
    console.log(this.formatMessage("DEBUG", message, context));
  }

  info(message: string, context?: Record<string, unknown>): void {
    console.log(this.formatMessage("INFO", message, context));
  }

  warn(message: string, context?: Record<string, unknown>): void {
    console.warn(this.formatMessage("WARN", message, context));
  }

  error(message: string, context?: Record<string, unknown>): void {
    console.error(this.formatMessage("ERROR", message, context));
  }

  fatal(message: string, context?: Record<string, unknown>): void {
    console.error(this.formatMessage("FATAL", message, context));
  }
}

/**
 * Default logger instance for packages
 * Server can replace this with its own logger implementation
 */
export const logger: Logger = new ConsoleLogger();

/**
 * Replace the default logger with a custom implementation
 * Used by server to inject its pino-based logger
 */
export function setLogger(customLogger: Logger): void {
  Object.assign(logger, customLogger);
}
